import RiskScoreBreakdown from './RiskScoreBreakdown';
import { useState } from 'react';
import { t, useLocale } from '../localization/adminText';
import { Banner, BlockStack, Button, InlineStack, Text, TextField } from './admin-ui';
import { u_cod_fulfillment_exemption } from '../api';

export default function CodOrderRiskDetails({ order, userToken, onChanged }) {
  useLocale();
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const risk = order.risk_assessment;
  if (!risk || risk.status === 'not_applicable') return null;
  const checkout = order.checkout_risk_assessment;
  const fulfillment = risk.rule_results?.fulfillment;
  const open = risk.rule_results?.open_orders;
  const openCount = ['insufficient_identity', 'unavailable', 'unreconciled'].includes(open?.status) || open?.ready === false ? '—' : open?.current_count ?? '—';
  const canExempt = order.can_manage_source !== false && order.list_membership?.can_manage !== false
    && ['matched', 'no_history'].includes(fulfillment?.status) && (fulfillment.mode !== 'off' || fulfillment.manual_exempt);
  const action = { allow: 'New orders allowed by the evaluated rules', block: 'New orders currently restricted', retry: 'Risk checks must be retried before a new order' };
  const mode = { off: 'Off', observe: 'Reminder only', enforce: 'Restrict new orders' };
  const changeExemption = async () => {
    if (saving || !reason.trim()) return;
    setSaving(true); setError(null);
    try {
      const response = await u_cod_fulfillment_exemption({ user_token: userToken, cod_order_id: order.id, exempt: !fulfillment.manual_exempt, reason: reason.trim() });
      if (!response.data?.risk_assessment || Number(response.status ?? response.code ?? 200) !== 200) throw new Error();
      await onChanged(response.data);
      setEditing(false); setReason('');
    } catch (failure) {
      const body = failure?.response?.data;
      setError(body?.message || body?.msg || 'Unable to change the fulfillment exemption.');
    } finally { setSaving(false); }
  };
  return <BlockStack gap="300">
    <Text as="h3" variant="headingSm">{t('Current risk decision')}</Text>
    <Text as="p">{t(action[risk.action] || 'Current risk decision unavailable')}</Text>
    <RiskScoreBreakdown assessment={risk} />
    <BlockStack gap="100">
      <Text as="h4" variant="headingSm">{t('Decision at checkout')}</Text>
      {checkout ? <>
        <Text as="p">{t({ allow: 'Checkout allowed', block: 'Checkout blocked', retry: 'Checkout verification incomplete' }[checkout.action] || 'Checkout verification incomplete')}{checkout.details_incomplete ? ` · ${t('Needs details')}` : ''}</Text>
        <Text as="p" tone="subdued">{t('Ordering behavior score: {0}', [checkout.behavior_score ?? '—'])}</Text>
        {checkout.invalid_phone?.count > 0 && <Text as="p" tone="subdued">{t('Invalid phone submissions in the last {0} minutes: {1} (+{2})', [checkout.invalid_phone.window_minutes, checkout.invalid_phone.count, checkout.invalid_phone.score])}</Text>}
        {checkout.reasons?.length > 0 && <Text as="p" tone="subdued">{checkout.reasons.map(item => t(item)).join(' · ')}</Text>}
      </> : <Text as="p" tone="subdued">{t('No checkout decision was recorded for this order.')}</Text>}
    </BlockStack>
    {risk.details_incomplete && <Text as="p" tone="subdued">{t('Phone or address details are incomplete. This alone does not restrict new orders; confirmed rule matches still apply.')}</Text>}
    {risk.status === 'unavailable' && <Text as="p" tone="subdued">{t('Some risk checks are unavailable; missing evidence is not a low-risk result.')}</Text>}
    {open?.mode && open.mode !== 'off' && <Text as="p">{t('Open order rule: {0} · Occupied places: {1} · Limit: {2}', [t(mode[open.mode]), openCount, open.limit ?? '—'])}</Text>}
    {fulfillment && <>
      <Text as="p">{t('Fulfillment rule: {0}', [t(mode[fulfillment.mode] || 'Off')])}</Text>
      {fulfillment.mode !== 'off' && <Text as="p" tone="subdued">{t('Required: {0} confirmed orders, {1} refusals and {2}% refusal rate.', [fulfillment.min_definitive_orders, fulfillment.min_refused_orders, Number(fulfillment.min_refusal_rate_bps) / 100])}</Text>}
      {fulfillment.manual_exempt && <Text as="p">{t('Fulfillment restriction exempted')}</Text>}
      {canExempt && <Button variant="plain" onClick={() => { setEditing(!editing); setError(null); }} disabled={saving}>
        {t(fulfillment.manual_exempt ? 'Restore fulfillment restriction' : 'Exempt fulfillment restriction')}
      </Button>}
      {editing && <BlockStack gap="200">
        <Text as="p">{t('This changes only the fulfillment restriction for this phone and address. Blacklists, frequency limits and open order limits still apply. The exemption remains until restored.')}</Text>
        <TextField label={t('Reason for this change')} value={reason} onChange={setReason} multiline maxLength={500} disabled={saving} autoComplete="off" />
        <InlineStack gap="200"><Button variant="primary" onClick={changeExemption} loading={saving} disabled={!reason.trim()}>{t('Confirm change')}</Button>
          <Button onClick={() => { setEditing(false); setReason(''); }} disabled={saving}>{t('Cancel')}</Button></InlineStack>
      </BlockStack>}
      {error && <Banner tone="critical">{t(error)}</Banner>}
    </>}
  </BlockStack>;
}
